import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLazyQuery, useMutation } from '@apollo/client'
import { Box, CircularProgress, Dialog, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import CheckIcon from '@mui/icons-material/Check'
import { colors } from '../../const/colors'
import { useSnackbar } from '../Shared/useSnackbar'
import { IQueryBasicData } from '../../graphql/IQueryData'
import {
  GET_PARKING_POOL_AVAILABILITY,
  IGetParkingPoolAvailabilityData,
  IGetParkingPoolAvailabilityVars,
  IParkingPoolAvailabilityDay,
} from '../../graphql/parkingPool/getParkingPoolAvailability'
import {
  GET_PARKING_POOL_RESERVATION_VARIANTS,
  IGetParkingPoolReservationVariantsData,
  IGetParkingPoolReservationVariantsVars,
} from '../../graphql/parkingPool/getParkingPoolReservationVariants'
import {
  ORDER_COMPANY_PARKING_TICKET_FOR_USER,
  IOrderCompanyParkingTicketForUserData,
  IOrderCompanyParkingTicketForUserVars,
} from '../../graphql/parkingPool/orderCompanyParkingTicketForUser'
import ReservationCalendar from './ReservationCalendar'
import ReservationVariantsPicker from './ReservationVariantsPicker'
import { ErrorBanner } from './reserveOnBehalfUi'
import {
  hasAvailableDay,
  noVariantsReasonKey,
} from './reservationVariantsPicker.utils'

const bannerSx = {
  mx: '28px',
  mb: '4px',
  p: '10px 14px',
  borderRadius: '11px',
  background: colors.redBg,
  color: colors.red,
  fontSize: '12.5px',
  fontWeight: 700,
}

const ReserveOnBehalfModal: FC<{
  open: boolean
  onClose: () => void
  login: string
  parkingPoolId: string
  userName: string
  bookedRanges?: { from: string; to: string }[]
  onConfirm: () => void
}> = ({
  open,
  onClose,
  login,
  parkingPoolId,
  userName,
  bookedRanges,
  onConfirm,
}) => {
  const { t } = useTranslation(['user_details', 'generic', 'code'])
  const { notifyError } = useSnackbar()

  const [selected, setSelected] = useState<string[]>([])
  const [availByDay, setAvailByDay] = useState<
    Record<string, IParkingPoolAvailabilityDay>
  >({})
  const [variantIdx, setVariantIdx] = useState<number | null>(null)
  const [notify, setNotify] = useState(true)
  const [actionError, setActionError] = useState<string | null>(null)
  const [orderingEnabledKnown, setOrderingEnabledKnown] = useState<
    boolean | null
  >(null)

  const [
    loadAvailability,
    { data: availData, loading: availLoading, error: availError },
  ] = useLazyQuery<
    IGetParkingPoolAvailabilityData,
    IGetParkingPoolAvailabilityVars
  >(GET_PARKING_POOL_AVAILABILITY, { fetchPolicy: 'no-cache' })

  const [
    loadVariants,
    { data: variantsData, loading: variantsLoading, error: variantsError },
  ] = useLazyQuery<
    IGetParkingPoolReservationVariantsData,
    IGetParkingPoolReservationVariantsVars
  >(GET_PARKING_POOL_RESERVATION_VARIANTS, { fetchPolicy: 'no-cache' })

  const [orderForUser, { loading: ordering }] = useMutation<
    IOrderCompanyParkingTicketForUserData,
    IOrderCompanyParkingTicketForUserVars
  >(ORDER_COMPANY_PARKING_TICKET_FOR_USER, {
    onError: error => {
      setActionError(error.message)
      notifyError(error)
    },
  })

  const availability = availData?.getParkingPoolAvailability
  const variantsPayload = variantsData?.getParkingPoolReservationVariants
  const maxDays = availability?.maxDurationDays

  const orderingFlag = (payload?: {
    status: boolean
    orderingEnabled: boolean
  }) => (payload?.status ? payload.orderingEnabled : null)

  const orderingAnswer =
    orderingFlag(availability) ?? orderingFlag(variantsPayload)

  const codeMessage = (code?: number | null) => {
    if (code === null || code === undefined) return null
    return t(`code:${code}`, { defaultValue: '' }) || null
  }

  const payloadError = (payload?: IQueryBasicData) =>
    payload && !payload.status
      ? codeMessage(payload.code) ||
        payload.error ||
        payload.message ||
        t('reserve_modal.error_generic')
      : null

  const availabilityError =
    availError?.message ?? payloadError(availability) ?? null

  const variantsErrorMessage =
    variantsError?.message ?? payloadError(variantsPayload) ?? null

  useEffect(() => {
    if (open) {
      setSelected([])
      setAvailByDay({})
      setVariantIdx(null)
      setNotify(true)
      setActionError(null)
      setOrderingEnabledKnown(null)
    }
  }, [open])

  useEffect(() => {
    const days = availability?.days
    if (!days?.length) return
    setAvailByDay(prev => {
      const next = { ...prev }
      days.forEach(day => {
        next[day.date.slice(0, 10)] = day
      })
      return next
    })
  }, [availability])

  useEffect(() => {
    if (orderingAnswer !== null) setOrderingEnabledKnown(orderingAnswer)
  }, [orderingAnswer])

  const availabilityDays = useMemo(
    () => Object.values(availByDay),
    [availByDay],
  )

  const handleMonthChange = useCallback(
    (from: string, to: string) => {
      if (!open) return
      loadAvailability({
        variables: { parkingPool: parkingPoolId, login, from, to },
      })
    },
    [open, parkingPoolId, login, loadAvailability],
  )

  const handleSelectedChange = useCallback((next: string[]) => {
    setSelected(next)
    setVariantIdx(null)
    setActionError(null)
  }, [])

  useEffect(() => {
    if (!selected.length) return
    loadVariants({ variables: { login, days: selected } })
  }, [selected, login, loadVariants])

  const sortedVariants = useMemo(() => {
    if (!variantsPayload?.status) return []
    return variantsPayload.variants
      .filter(hasAvailableDay)
      .sort((a, b) => b.score - a.score)
  }, [variantsPayload])

  const noVariantsKey = variantsPayload?.status
    ? noVariantsReasonKey(variantsPayload.variants)
    : null

  useEffect(() => {
    if (sortedVariants.length && variantIdx === null) setVariantIdx(0)
  }, [sortedVariants, variantIdx])

  const canCreate =
    orderingEnabledKnown === true &&
    !availabilityError &&
    !!selected.length &&
    variantIdx !== null &&
    !ordering &&
    !!sortedVariants[variantIdx]

  const handleConfirm = async () => {
    if (!canCreate || variantIdx === null) return
    setActionError(null)
    const chosen = sortedVariants[variantIdx]
    const res = await orderForUser({
      variables: {
        login,
        days: chosen.days.map(d => d.day),
        variant: chosen.days.map(d => ({
          day: d.day,
          pool_parking_id: d.poolParkingId,
        })),
        notify,
      },
    })
    const payload = res.data?.orderCompanyParkingTicketForUser
    if (payload?.status) onConfirm()
    else if (payload) {
      const message = payloadError(payload) ?? t('reserve_modal.error_generic')
      setActionError(message)
      notifyError(message)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: colors.radiusModal,
          width: 940,
          maxWidth: '100%',
          maxHeight: '90vh',
          m: 2,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: '22px 26px 16px',
          flexShrink: 0,
        }}
      >
        <Box>
          <Typography
            sx={{ fontSize: '20px', fontWeight: 800, letterSpacing: '-0.01em' }}
          >
            {t('reserve_modal.title')}
          </Typography>
          <Typography
            sx={{ fontSize: '13px', color: colors.textMuted, mt: '3px' }}
          >
            {userName}
          </Typography>
        </Box>
        <Box
          onClick={onClose}
          sx={{
            width: 34,
            height: 34,
            borderRadius: '50%',
            background: '#EFF3F6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: colors.textSecondary,
            flexShrink: 0,
            '&:hover': { background: '#E1E9EE' },
          }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </Box>
      </Box>

      {(availabilityError || actionError) && (
        <ErrorBanner
          heading={t('reserve_modal.error_heading')}
          message={(availabilityError || actionError) as string}
          sx={{ mx: '28px', mb: '10px' }}
        />
      )}

      {orderingEnabledKnown === false && (
        <Box sx={bannerSx}>{t('reserve_modal.ordering_disabled')}</Box>
      )}

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: '4px 28px 8px',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: '28px',
          alignItems: 'start',
        }}
      >
        <ReservationCalendar
          availabilityDays={availabilityDays}
          loading={availLoading}
          maxDays={maxDays}
          maxReservationsPerUser={availability?.maxReservationCountPerUser}
          bookedRanges={bookedRanges}
          selected={selected}
          onSelectedChange={handleSelectedChange}
          onMonthChange={handleMonthChange}
        />

        <ReservationVariantsPicker
          selectedCount={selected.length}
          variants={sortedVariants}
          loading={variantsLoading}
          error={variantsErrorMessage}
          variantIdx={variantIdx}
          onSelect={setVariantIdx}
          noVariantsReason={noVariantsKey ? t(noVariantsKey) : null}
        />
      </Box>
      <Box
        sx={{
          flexShrink: 0,
          p: '16px 28px 22px',
          borderTop: '1px solid #EFF3F6',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '18px',
          flexWrap: 'wrap',
        }}
      >
        <Box
          onClick={() => setNotify(v => !v)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '11px',
            cursor: 'pointer',
          }}
        >
          <Box
            sx={{
              width: 22,
              height: 22,
              borderRadius: '7px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              ...(notify
                ? {
                    background: colors.navy,
                    border: `1.5px solid ${colors.navy}`,
                  }
                : {
                    background: '#fff',
                    border: `1.5px solid ${colors.borderDash}`,
                  }),
            }}
          >
            {notify && <CheckIcon sx={{ fontSize: 13, color: '#fff' }} />}
          </Box>
          <Typography
            sx={{
              fontSize: '13.5px',
              color: colors.textAccent,
              fontWeight: 600,
            }}
          >
            {t('reserve_modal.notify_user')}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            gap: '10px',
            flex: 1,
            justifyContent: 'flex-end',
            minWidth: 240,
          }}
        >
          <Box
            onClick={handleConfirm}
            data-e2e="create-reservation"
            aria-disabled={!canCreate}
            sx={{
              height: 50,
              px: '36px',
              borderRadius: '14px',
              fontWeight: 800,
              fontSize: '14.5px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '9px',
              transition: 'background .15s',
              ...(canCreate
                ? {
                    background: colors.greenMuted,
                    color: colors.navy,
                    cursor: 'pointer',
                    boxShadow: colors.shadowKpiGreen,
                    '&:hover': { background: '#9BBE9C' },
                  }
                : {
                    background: colors.bg,
                    color: colors.slateLight,
                    cursor: 'not-allowed',
                  }),
            }}
          >
            {ordering ? (
              <CircularProgress size={18} sx={{ color: colors.slateLight }} />
            ) : (
              <CheckIcon sx={{ fontSize: 18 }} />
            )}
            {t('reserve_modal.create_reservation')}
          </Box>
        </Box>
      </Box>
    </Dialog>
  )
}

export default ReserveOnBehalfModal
